import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import {
  AnyAction,
  Dispatch,
  ListenerEffect,
  ThunkAction,
  ThunkDispatch,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import { attachmentsApi, attachmentsApiListener } from 'attachment/api';
import { communityApi, communityApiListener } from 'community/api';
import { communitySlice } from 'community/state/communitySlice';
import { communityListener } from 'community/state/listener';
import { mediaSettingsListener, mediaSettingsReducer } from 'devices/state';
import { insightsReportsSlice } from 'insightsReport/state';
import { alertsListener, alertsSlice } from 'modules/alerts/state';
import { configService } from 'modules/app/services';
import { appSlice } from 'modules/app/state';
import { authenticationApi } from 'modules/authentication/api';
import { keycloakService } from 'modules/keycloak';
import { routerMiddleware, routerReducer } from 'modules/router/state';
import { Socket, socketMiddleware, socketSlice } from 'modules/webSocket/state';
import { natalieFeedbackApi } from 'natalie/api/feedbackApi';
import { natalieEventApi } from 'natalie/api/natalieEventApi';
import { openNatterApi, openNatterApiListener } from 'openNatter/api';
import { openNatterSocketMiddleware } from 'openNatter/socket';
import { openNatterListener } from 'openNatter/state/openNatter.listener';
import { openNatterReducer } from 'openNatter/state/openNatter.slice';
import { organizationApi, organizationApiListener } from 'organization/api';
import { roomsSearchListener, roomsSearchSlice } from 'pages/Backstage/state';
import { chatListener, chatSlice } from 'pages/EventBroadcast/state/chat';
import { participantsSearchListener, participantsSearchSlice } from 'pages/EventBroadcast/state/participantsSearch';
import { reactionsListener, reactionsSlice } from 'pages/EventBroadcast/state/reactions';
import { eventWizardListener, eventWizardReducer } from 'pages/EventWizard/state';
import { insightsReportsWizardListener, insightsReportsWizardSlice } from 'pages/InsightsReportsWizard/state';
import { masterAdminSlice } from 'pages/MasterAdmin/state';
import { natalieListener } from 'pages/Natalie/state/natalie.listener';
import { eventQueueListener } from 'pages/NatteringRoom/state';
import { surveysWizardListener, surveysWizardReducer } from 'pages/SurveyWizard/state';
import { surveysListListener } from 'pages/Surveys/state';
import { techCheckApi, techCheckApiListener } from 'pages/TechCheck/api';
import { answerApi } from 'question/api';
import { questionSlice, questionsListener } from 'question/state';
import { tagsApi } from 'tags';
import { tagServiceApi } from 'tags/tagServiceApi';
import { userApi, userApiListener } from 'user/api';
import { userSlice } from 'user/state';
import { userListener } from 'user/state/listener';

import { activeEventListener, eventApi, eventSeriesApi } from './apis/event';
import { eventsReportApi, eventsReportApiLister } from './apis/eventsReport';
import { insightsReportApi, insightsReportApiLister } from './apis/insightsReport';
import { intercomApi } from './apis/intercom';
import { nattersApi, nattersApiLister, nattersApiMonitoringListener } from './apis/natters';
import { publicEventsReportApi, publicEventsReportApiLister } from './apis/publicEventsReport';
import { publicSurveysReportApi, publicSurveysReportApiLister } from './apis/publicSurveysReport';
import { surveyApi, surveyApiLister } from './apis/survey';
import { surveyReportApi, surveyReportApiLister } from './apis/surveyReport';
import { agoraReducer } from './features/agora';
import { agoraListener } from './features/agora/agora.listener';
import { eventSlice, meetingListener, roomListener } from './features/event';

const sentryReduxEnhancer = createReduxEnhancer();

export const rootReducer = combineReducers({
  router: routerReducer,
  [socketSlice.name]: socketSlice.reducer,
  [reactionsSlice.name]: reactionsSlice.reducer,
  [eventApi.reducerPath]: eventApi.reducer,
  [eventSeriesApi.reducerPath]: eventSeriesApi.reducer,
  [insightsReportApi.reducerPath]: insightsReportApi.reducer,
  [surveyReportApi.reducerPath]: surveyReportApi.reducer,
  [publicSurveysReportApi.reducerPath]: publicSurveysReportApi.reducer,
  [eventsReportApi.reducerPath]: eventsReportApi.reducer,
  [publicEventsReportApi.reducerPath]: publicEventsReportApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [attachmentsApi.reducerPath]: attachmentsApi.reducer,
  [communityApi.reducerPath]: communityApi.reducer,
  [surveyApi.reducerPath]: surveyApi.reducer,
  [nattersApi.reducerPath]: nattersApi.reducer,
  [openNatterApi.reducerPath]: openNatterApi.reducer,
  [natalieEventApi.reducerPath]: natalieEventApi.reducer,
  [natalieFeedbackApi.reducerPath]: natalieFeedbackApi.reducer,
  [eventSlice.name]: eventSlice.reducer,
  openNatter: openNatterReducer,
  [participantsSearchSlice.name]: participantsSearchSlice.reducer,
  [roomsSearchSlice.name]: roomsSearchSlice.reducer,
  [alertsSlice.name]: alertsSlice.reducer,
  [userSlice.name]: userSlice.reducer,
  [appSlice.name]: appSlice.reducer,
  [chatSlice.name]: chatSlice.reducer,
  [masterAdminSlice.name]: masterAdminSlice.reducer,
  [communitySlice.name]: communitySlice.reducer,
  [insightsReportsSlice.name]: insightsReportsSlice.reducer,
  [techCheckApi.reducerPath]: techCheckApi.reducer,
  [answerApi.reducerPath]: answerApi.reducer,
  [authenticationApi.reducerPath]: authenticationApi.reducer,
  [intercomApi.reducerPath]: intercomApi.reducer,
  [tagsApi.reducerPath]: tagsApi.reducer,
  [tagServiceApi.reducerPath]: tagServiceApi.reducer,
  question: questionSlice,
  eventWizard: eventWizardReducer,
  [insightsReportsWizardSlice.name]: insightsReportsWizardSlice.reducer,
  surveyWizard: surveysWizardReducer,
  agora: agoraReducer,
  mediaSettings: mediaSettingsReducer,
});

export const setupStore = (
  preloadedState?: Partial<RootState>,
  socket = new Socket({ keycloakService, configService })
) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Ignore these paths in the state from serializability checks
          // RTK Query stores originalArgs in cache which may contain non-serializable values:
          // - File objects (image/file uploads)
          // - Blob objects (event images)
          // - FormData objects (multipart form uploads)
          // These are needed for upload operations and are not persisted
          ignoredPaths: [
            'eventApi.queries',
            'eventApi.mutations',
            'eventSeriesApi.mutations',
            'attachmentsApi.queries',
            'attachmentsApi.mutations',
            'userApi.mutations',
            'communityApi.mutations',
            'organizationApi.mutations',
          ],
        },
      })
        .concat(meetingListener.middleware)
        .concat(roomListener.middleware)
        .concat(openNatterListener.middleware)
        .concat(openNatterSocketMiddleware)
        .concat(activeEventListener.middleware)
        .concat(participantsSearchListener.middleware)
        .concat(roomsSearchListener.middleware)
        .concat(communityApiListener.middleware)
        .concat(eventApi.middleware)
        .concat(eventSeriesApi.middleware)
        .concat(userApi.middleware)
        .concat(userApiListener.middleware)
        .concat(communityApi.middleware)
        .concat(routerMiddleware)
        .concat(organizationApi.middleware)
        .concat(communityListener.middleware)
        .concat(organizationApiListener.middleware)
        .concat(questionsListener.middleware)
        .concat(reactionsListener.middleware)
        .concat(chatListener.middleware)
        .concat(insightsReportApi.middleware)
        .concat(surveyReportApi.middleware)
        .concat(publicSurveysReportApi.middleware)
        .concat(eventsReportApi.middleware)
        .concat(publicEventsReportApi.middleware)
        .concat(surveyApi.middleware)
        .concat(surveysWizardListener.middleware)
        .concat(eventWizardListener.middleware)
        .concat(insightsReportApiLister.middleware)
        .concat(surveyApiLister.middleware)
        .concat(surveysListListener.middleware)
        .concat(eventQueueListener.middleware)
        .concat(userListener.middleware)
        .concat(eventsReportApiLister.middleware)
        .concat(publicEventsReportApiLister.middleware)
        .concat(surveyReportApiLister.middleware)
        .concat(publicSurveysReportApiLister.middleware)
        .concat(insightsReportsWizardListener.middleware)
        .concat(attachmentsApi.middleware)
        .concat(attachmentsApiListener.middleware)
        .concat(nattersApi.middleware)
        .concat(nattersApiLister.middleware)
        .concat(openNatterApi.middleware)
        .concat(openNatterApiListener.middleware)
        .concat(nattersApiMonitoringListener.middleware)
        .concat(techCheckApi.middleware)
        .concat(techCheckApiListener.middleware)
        .concat(answerApi.middleware)
        .concat(agoraListener.middleware)
        .concat(mediaSettingsListener.middleware)
        .concat(natalieListener.middleware)
        .concat(natalieEventApi.middleware)
        .concat(natalieFeedbackApi.middleware)
        .concat(alertsListener.middleware)
        .concat(authenticationApi.middleware)
        .concat(intercomApi.middleware)
        .concat(tagsApi.middleware)
        .concat(tagServiceApi.middleware)
        .concat(socketMiddleware(socket)),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  });

export const store = setupStore();

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;

export type AppDispatch = typeof store.dispatch;

export type CombinedDispatch = Dispatch & ThunkDispatch<RootState, unknown, AnyAction>;

export type Effect<Action extends AnyAction = AnyAction> = ListenerEffect<Action, RootState, CombinedDispatch>;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;
